'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog'

import { MEETINGS_PAGE_URL } from './meetings-url'

const MEETINGS_EMBED_SCRIPT_SRC = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'

/** Nothing to subscribe to: the "store" is just "are we in the browser yet". */
const noopSubscribe = () => () => {}

interface BookMeetingButtonProps {
  children?: React.ReactNode
  className?: string
  variant?: 'link' | 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  /** Render `children` as the trigger element instead of wrapping it in a `Button`. */
  asChild?: boolean
  /**
   * Render the trigger as a real link to this URL (SCH-2162 KTD7). With
   * JavaScript the click opens the modal instead of navigating; without it, the
   * link still reaches the scheduler. `className` styles the link.
   */
  href?: string
  /** The HubSpot meetings page to embed. Defaults to Jamie's demo page. */
  meetingUrl?: string
  /** Screen-reader title for the dialog. Defaults to "Book a demo". */
  dialogTitle?: string
  /** Screen-reader description for the dialog. */
  dialogDescription?: string
}

/**
 * Injects HubSpot's loader while the dialog is open.
 *
 * HubSpot's loader scans the DOM for `.meetings-iframe-container` at the moment it executes, and the container
 * only exists while the dialog is mounted — so the script is appended fresh on each open (a new `<script>`
 * element always re-executes, which re-runs the scan) and removed again on close.
 */
function useMeetingsEmbed(open: boolean, container: HTMLDivElement | null) {
  useEffect(() => {
    if (!open || !container) return

    // Guard against a second injection into a container the loader already filled.
    if (container.childElementCount > 0) return

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = MEETINGS_EMBED_SCRIPT_SRC
    document.body.appendChild(script)

    return () => {
      script.remove()
      container.replaceChildren()
    }
  }, [open, container])
}

/** A click the browser would handle as "open here", not as "open in a new tab or window". */
function isPlainClick(event: React.MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

/**
 * CTA button that opens the HubSpot Meetings scheduler in a modal.
 *
 * The container is tracked in state rather than a ref: Radix portals the dialog content, so it has not committed
 * yet when the effect from the `open` flip runs. A ref would still read `null` there and — with `open` as the only
 * dependency — the effect would never re-run once the node appeared, so the script was never injected and the
 * modal stayed blank. A state setter as `ref` re-renders when the node attaches, which re-runs the effect.
 *
 * THE `href` TRIGGER sits OUTSIDE Radix's `DialogTrigger`. Composing it with `asChild` cannot work: the
 * `preventDefault` that stops the navigation also suppresses the trigger's own open. So the link opens the
 * controlled dialog itself, carries the `aria-haspopup` / `aria-expanded` a `DialogTrigger` would, and gets focus
 * back on close through `onCloseAutoFocus`, since Radix only restores focus to its own trigger.
 *
 * THE IN-MODAL LINK is always visible, above the embed, so a reader whose blocker stops HubSpot's loader still has
 * a way to the scheduler (SCH-2162 R10).
 */
export function BookMeetingButton({
  children,
  className,
  variant = 'default',
  size,
  asChild = false,
  href,
  meetingUrl = MEETINGS_PAGE_URL,
  dialogTitle = 'Book a demo',
  dialogDescription = 'Pick a time that works for you and book a School Journey demo.'
}: BookMeetingButtonProps) {
  const [open, setOpen] = useState(false)
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const linkTrigger = useRef<HTMLAnchorElement>(null)
  useMeetingsEmbed(open, container)

  const openFromLink = (event: React.MouseEvent) => {
    if (!isPlainClick(event)) return
    event.preventDefault()
    setOpen(true)
  }

  const returnFocusToLink = (event: Event) => {
    event.preventDefault()
    linkTrigger.current?.focus()
  }

  // Radix gives the trigger a generated `aria-controls` id, and on the marketing pages the server and browser
  // generate different ones (a hydration mismatch on every demo button). Until mounted, render the same trigger
  // without the dialog; the dialog's wiring attaches in the browser. With `href`, the link still works pre-mount.
  // useSyncExternalStore reports false while hydrating and true after, without a setState-in-effect re-render.
  const mounted = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  )
  if (!mounted) {
    if (href) {
      return (
        <a href={href} className={className}>
          {children}
        </a>
      )
    }
    return asChild ? (
      <>{children}</>
    ) : (
      <Button className={className} variant={variant} size={size}>
        {children || 'Book a demo'}
      </Button>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {href ? (
        <a
          ref={linkTrigger}
          href={href}
          className={className}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={openFromLink}
        >
          {children}
        </a>
      ) : (
        <DialogTrigger asChild>
          {asChild ? (
            children
          ) : (
            <Button className={className} variant={variant} size={size}>
              {children || 'Book a demo'}
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent
        onCloseAutoFocus={href ? returnFocusToLink : undefined}
        className="sm:max-w-[425px] md:max-w-[640px] lg:max-w-[768px] xl:max-w-[900px] max-h-[90vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">{dialogTitle}</DialogTitle>
        <DialogDescription className="sr-only">{dialogDescription}</DialogDescription>
        <a
          href={meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="justify-self-start pr-8 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Open the scheduler in a new tab
        </a>
        <div
          ref={setContainer}
          className="meetings-iframe-container min-h-[700px] w-full"
          data-src={`${meetingUrl}?embed=true`}
        />
      </DialogContent>
    </Dialog>
  )
}
